import { useState, useEffect } from "react";
import { Check, Crown, Star, Zap, Building2, Users, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { TierCard, SponsorTierCard } from "./TierCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MembershipModal = ({ isOpen, onClose }) => {
  const [tiers, setTiers] = useState([]);
  const [foundingSpotsLeft, setFoundingSpotsLeft] = useState(20);
  const [taxRate, setTaxRate] = useState(0.05);
  const [taxLabel, setTaxLabel] = useState("GST");
  const [selectedTier, setSelectedTier] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => { if (isOpen) fetchTiers(); }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTiers = async () => {
    try {
      const res = await axios.get(`${API}/membership/all-tiers`);
      setTiers(res.data.tiers.filter(t => t.price_yearly > 0));
      setFoundingSpotsLeft(res.data.founding_spots_remaining ?? 20);
      if (res.data.tax) { setTaxRate(res.data.tax.rate); setTaxLabel(res.data.tax.label); }
    } catch (e) { console.error(e); }
  };

  const handleSubscribe = async () => {
    if (!selectedTier || !email) { toast.error("Select a tier and enter your email"); return; }
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/membership/subscribe`, {
        user_email: email, user_name: name || null,
        tier_name: selectedTier, origin_url: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to process"); setProcessing(false); }
  };

  const currentTier = tiers.find(t => t.name === selectedTier);
  const subtotal = currentTier?.price_yearly || 0;
  const tax = Math.round(subtotal * taxRate * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#EFEDE5]">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-[#1A1915]">Join the EK Valleys Network</DialogTitle></DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {tiers.map((tier) => (
            <TierCard
              key={tier.name}
              tier={tier}
              isSelected={selectedTier === tier.name}
              onClick={setSelectedTier}
              isFounding={tier.name === "founding"}
              foundingSpotsLeft={foundingSpotsLeft}
            />
          ))}
        </div>

        {/* Form */}
        <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-white" required data-testid="membership-email" />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-white" data-testid="membership-name" />
            </div>
          </div>
        </div>

        {selectedTier && (
          <div className="bg-[#1A2F23] text-white rounded-xl p-4 text-sm">
            <div className="flex justify-between"><span className="text-white/70">Subtotal</span><span>${subtotal}/yr</span></div>
            <div className="flex justify-between"><span className="text-white/70">{taxLabel} ({(taxRate * 100).toFixed(0)}%)</span><span>${tax.toFixed(2)}</span></div>
            <div className="border-t border-white/20 mt-2 pt-2 flex justify-between font-bold"><span>Total</span><span>${total}/yr</span></div>
          </div>
        )}

        <div className="flex justify-end gap-4 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubscribe} disabled={processing || !selectedTier || !email}
            className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-8"
            data-testid="membership-subscribe-btn">
            {processing ? "Processing..." : "Subscribe"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SponsorModal = ({ isOpen, onClose }) => {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => { if (isOpen) fetchTiers(); }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const fetchTiers = async () => { try { const r = await axios.get(`${API}/sponsor/tiers`); setTiers(r.data.tiers); } catch { /* expected */ } };

  const handleSubscribe = async () => {
    if (!selectedTier || !email || !companyName) { toast.error("Fill in all required fields"); return; }
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/sponsor/subscribe`, { user_email: email, user_name: name, company_name: companyName, tier_name: selectedTier, origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setProcessing(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#EFEDE5]">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-[#1A1915]">Become a Sponsor</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {tiers.map((tier) => (
            <SponsorTierCard key={tier.name} tier={tier} isSelected={selectedTier === tier.name} onClick={setSelectedTier} />
          ))}
        </div>
        <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Company *</Label><Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-1 bg-white" required /></div>
            <div><Label>Email *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-white" required /></div>
            <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-white" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-4 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubscribe} disabled={processing || !selectedTier || !email || !companyName} className="bg-[#1E3A5F] hover:bg-[#152B47] text-white font-semibold px-8" data-testid="sponsor-subscribe-btn">
            {processing ? "Processing..." : "Become Sponsor"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MembershipModal;
